import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"
import { resolveRunyiStatus, runyiStatusDotClasses, runyiStatusToneClasses } from "@/lib/status-indicator"

const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "bg-destructive text-white focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90",
        outline:
          "border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        ghost: "[a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        link: "text-primary underline-offset-4 [a&]:hover:underline",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function simpleStatusText(children: React.ReactNode) {
  if (typeof children === "string" || typeof children === "number") return String(children)
  return null
}

function hasLegacySemanticTone(className?: string) {
  if (!className) return false
  return /(?:^|\s)(?:dark:)?(?:bg|text|border)-(?:lime|green|emerald|blue|sky|cyan|violet|purple|orange|amber|yellow|red)-/i.test(className)
}

type BadgeProps = React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean
    status?: string | null
    showStatusDot?: boolean
  }

function Badge({
  className,
  variant = "default",
  asChild = false,
  status: explicitStatus,
  showStatusDot = true,
  children,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot.Root : "span"
  const labelText = simpleStatusText(children)
  const statusText = explicitStatus ?? labelText
  const resolved = resolveRunyiStatus(statusText)
  const legacyNeutralized = !asChild && Boolean(labelText) && !resolved.recognized && hasLegacySemanticTone(className)
  const status = legacyNeutralized
    ? { ...resolved, canonicalToken: "UNKNOWN", tone: "neutral" as const, recognized: true }
    : resolved
  const applyStatus = !asChild && Boolean(statusText) && status.recognized
  const statusSource = applyStatus
    ? explicitStatus !== undefined
      ? "explicit"
      : legacyNeutralized
        ? "legacy-neutralized"
        : "label"
    : undefined

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      data-runyi-status={applyStatus ? "true" : undefined}
      data-status-tone={applyStatus ? status.tone : undefined}
      data-status-token={applyStatus ? status.token : undefined}
      data-status-canonical-token={applyStatus ? status.canonicalToken : undefined}
      data-status-source={statusSource}
      className={cn(
        badgeVariants({ variant }),
        className,
        applyStatus && runyiStatusToneClasses[status.tone],
      )}
      {...props}
    >
      {applyStatus && showStatusDot && (
        <span
          aria-hidden="true"
          className={cn("size-1.5 shrink-0 rounded-full", runyiStatusDotClasses[status.tone])}
        />
      )}
      {children}
    </Comp>
  )
}

export { Badge, badgeVariants }
